import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Service Areas', path: '/service-areas' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'General Pest Control', path: '/services#general' },
    { name: 'Termite Control', path: '/services#termite' },
    { name: 'Rodent Control', path: '/services#rodent' },
    { name: 'Bed Bug Treatment', path: '/services#bedbug' },
    { name: 'Commercial Services', path: '/services#commercial' },
  ];

  const policies = [
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'Disclaimer', path: '/disclaimer' },
    { name: 'Cookie Policy', path: '/cookie-policy' },
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-[#1A1C1A] text-white" data-testid="footer">
      <div className="container-main py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#2E7D32] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Primes Pick
              </span>
            </Link>
            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
              Professional pest control services for a safe and pest-free environment. 
              Protecting homes and businesses across the United States since 2010.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#2E7D32] transition-colors duration-200"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-[#2E7D32] transition-colors duration-200 footer-link inline-block text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Services
            </h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-gray-400 hover:text-[#2E7D32] transition-colors duration-200 footer-link inline-block text-sm"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Contact Us
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                <a href="tel:+18883568891" className="text-gray-400 hover:text-white transition-colors text-sm">
                  +1-888-356-8891
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                <a href="mailto:support@primespick.store" className="text-gray-400 hover:text-white transition-colors text-sm break-all">
                  support@primespick.store
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  1284 Silver Lake Road<br />
                  Minneapolis, MN 44401
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  Mon - Sat: 8:00 AM - 6:00 PM<br />
                  Sunday: Closed
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mt-10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Policy Links */}
            <div className="flex flex-wrap justify-center md:justify-start gap-4 sm:gap-6">
              {policies.map((policy) => (
                <Link
                  key={policy.path}
                  to={policy.path}
                  className="text-gray-400 hover:text-white transition-colors text-xs sm:text-sm"
                  data-testid={`footer-policy-${policy.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {policy.name}
                </Link>
              ))}
            </div>

            {/* Copyright */}
            <p className="text-gray-500 text-xs sm:text-sm text-center md:text-right">
              &copy; {currentYear} Primes Pick. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
